// src/components/layout/Navbar.tsx
// Main navigation bar - single-page smooth-scroll navigation

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button'; // Use shadcn Button for mobile toggle
import { Menu, X } from 'lucide-react'; // Import icons
import { ThemeToggle } from '@/components/layout/ThemeToggle'; // Import ThemeToggle
import { LanguageToggle } from '@/components/layout/LanguageToggle'; // Import LanguageToggle
import TifpaLogo from '@/components/ui/TifpaLogo';
import { scrollToSection, scrollToTop } from '@/lib/scroll';

// Section targets on the single-page site
const NAV_LINKS: { label: string; sectionId: string | null }[] = [
  { label: 'Home', sectionId: null }, // null = scroll to top
  { label: 'Services', sectionId: 'services' },
  { label: 'Contact', sectionId: 'consultation' },
];

/**
 * Main Navbar component with scroll effect, mobile menu toggle, theme toggle, and language toggle.
 * The site is a single page, so nav links smooth-scroll to page sections.
 */
const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Effect for scroll detection
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const handleNavClick = (sectionId: string | null) => {
    closeMenu();
    if (sectionId) {
      scrollToSection(sectionId);
    } else {
      scrollToTop();
    }
  };

  return (
    <header
      // Apply dynamic background/padding based on scroll state
      className="fixed w-full z-50 transition-all duration-300 bg-white/0 py-4"
    >
      <div
        className={`container mx-auto flex items-center justify-between px-4 md:px-6 lg:px-8 rounded-lg transition-all duration-300 ${
          isScrolled
            ? 'bg-white/70 shadow-md py-2 border-border backdrop-blur-sm' // Scrolled state: White background
            : 'bg-white/0 py-4' // Top state
        }`}
      >
        {/* Left Side: Logo */}
        <button
          type="button"
          className="text-xl md:text-2xl"
          onClick={() => handleNavClick(null)}
          aria-label="Go to top"
        >
          <TifpaLogo />
        </button>

        {/* Right Side (Desktop) */}
        {/* Ensure link text color has contrast on white background */}
        <nav className="hidden lg:flex items-center space-x-6">
          {NAV_LINKS.map(link => (
            <button
              key={link.label}
              type="button"
              className="font-body font-medium text-stone-700 hover:text-tifpa-rose transition-all"
              onClick={() => handleNavClick(link.sectionId)}
            >
              {link.label}
            </button>
          ))}

          {/* Language Toggle Button */}
          {/* <LanguageToggle /> */}
          {/* Theme Toggle Button */}
          {/* <ThemeToggle /> */}
        </nav>

        {/* Mobile Menu Toggle Button */}
        <div className="flex items-center lg:hidden">
          {/* Wrapper for mobile buttons */}
          <LanguageToggle /> {/* Language toggle for mobile */}
          <ThemeToggle /> {/* Theme toggle for mobile */}
          <Button
            variant="ghost"
            size="icon"
            className="text-stone-800 hover:bg-stone-100 ml-1" // Reduced margin
            aria-label="Toggle menu"
            onClick={toggleMenu}
          >
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </Button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {/* Mobile menu uses theme background, which is fine */}
      {isMenuOpen && (
        <div className="absolute top-full left-0 right-0 bg-background shadow-md p-4 flex flex-col gap-4 lg:hidden border-t border-border animate-fade-in">
          {NAV_LINKS.map(link => (
            <button
              key={link.label}
              type="button"
              className="font-body font-medium text-foreground hover:text-tifpa-rose py-2 text-left"
              onClick={() => handleNavClick(link.sectionId)}
            >
              {link.label}
            </button>
          ))}
        </div>
      )}
    </header>
  );
};

export default Navbar;
